"use client";

import { useScreenSizeEffect } from "@pharaday/utils";
import { Command, defaultFilter } from "cmdk";
import { type ComponentProps, useCallback, useEffect, useMemo, useState } from "react";

import { cn } from "../../utils/cn.util";
import { Popover } from "../popover";
import { ComboboxContext } from "./context";
import useComboboxCursor from "./useComboboxCursor";

const DEFAULT_FUZZY_THRESHOLD = 0.05;

export interface ComboboxRootProps extends ComponentProps<typeof Command> {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  multiple?: boolean;
  withoutPopover?: boolean;
  /**
   * Minimum `command-score` an option must reach to be considered a match. Ignored when a custom `filter` is supplied.
   */
  fuzzyThreshold?: number;
}

const Root = ({
  ref,
  className,
  children,
  multiple = false,
  withoutPopover = false,
  shouldFilter = true,
  fuzzyThreshold = DEFAULT_FUZZY_THRESHOLD,
  filter,
  label,
  onOpenChange,
  value,
  onValueChange,
  ...props
}: ComboboxRootProps) => {
  const [open, setOpen] = useState(props?.open || false);
  const isMobile = useScreenSizeEffect();

  const contentVisible = withoutPopover || open;
  // cmdk's *active* row (the one arrow keys move) and the scrolling that keeps it in view.
  const { activeValue, listRef, prepareOpenChange, handleValueChange, reportSelectedItem, notifySearchChanged } =
    useComboboxCursor({ contentVisible, onValueChange });

  useEffect(() => {
    setOpen(!!props?.open);
  }, [props?.open]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      prepareOpenChange(open);
      onOpenChange?.(open);
      setOpen(open);
    },
    [onOpenChange, prepareOpenChange]
  );

  useEffect(() => {
    if (open) {
      handleOpenChange(false);
    }
  }, [isMobile]);

  // cmdk's own fuzzy scoring (typo tolerance, abbreviations like "chfmate", and the relevance ranking that
  // orders the list), with matches weaker than `fuzzyThreshold` dropped.
  const effectiveFilter = useMemo<NonNullable<ComboboxRootProps["filter"]>>(
    () =>
      filter ??
      ((value, search, keywords) => {
        const score = defaultFilter(value, search, keywords);
        return score >= fuzzyThreshold ? score : 0;
      }),
    [filter, fuzzyThreshold]
  );

  const providerValue = useMemo(
    () => ({
      open,
      setOpen: handleOpenChange,
      multiple,
      withoutPopover,
      label,
      isMobile,
      listRef,
      reportSelectedItem,
      notifySearchChanged,
    }),
    [
      open,
      multiple,
      withoutPopover,
      label,
      handleOpenChange,
      isMobile,
      listRef,
      reportSelectedItem,
      notifySearchChanged,
    ]
  );

  return (
    <>
      <style>
        {`
          div[cmdk-list-sizer] {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }
        `}
      </style>
      <ComboboxContext value={providerValue}>
        <Command
          ref={ref}
          className={cn(className)}
          shouldFilter={shouldFilter}
          filter={effectiveFilter}
          // A consumer-controlled value wins; otherwise useComboboxCursor drives the cursor.
          value={value ?? activeValue}
          onValueChange={handleValueChange}
          {...props}
        >
          {withoutPopover || isMobile ? (
            children
          ) : (
            <Popover.Root open={open} onOpenChange={handleOpenChange}>
              {children}
            </Popover.Root>
          )}
        </Command>
      </ComboboxContext>
    </>
  );
};

Root.displayName = Command.displayName || "Root";

export default Root;
